import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BrandReportDay } from "../../schemas/brandReportSchema";
import {
  formatAirtime,
  formatDayLabel,
} from "../../utils/brandReport";

const BAR_COLOR = "#1d4ed8"; // primary-default

type DailyMetric = "plays" | "airtime";

/**
 * Plays (or airtime) per day across the window. A day with nothing on it is a
 * real zero from the server, not a gap, so a dip reads as a dip.
 */
export const DailyDeliveryChart: React.FC<{ days: BrandReportDay[] }> = ({
  days,
}) => {
  const [metric, setMetric] = useState<DailyMetric>("plays");

  const data = useMemo(
    () =>
      days.map((day) => ({
        date: day.date,
        label: formatDayLabel(day.date),
        // Airtime is charted in minutes: seconds make an unreadable axis.
        value: metric === "plays" ? day.plays : Math.round(day.airtime / 60),
        plays: day.plays,
        airtime: day.airtime,
        screens: day.screens,
      })),
    [days, metric],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-800">Daily delivery</h2>
        <div
          role="group"
          aria-label="Chart metric"
          className="flex overflow-hidden rounded border border-gray-300 text-xs"
        >
          {(
            [
              { value: "plays", label: "Plays" },
              { value: "airtime", label: "Airtime (min)" },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={metric === option.value}
              onClick={() => setMetric(option.value)}
              className={`px-2.5 py-1 font-medium transition ${
                metric === option.value
                  ? "bg-primary-default text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">
          This window has not started yet.
        </p>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid vertical={false} stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={48}
              />
              <Tooltip
                cursor={{ fill: "rgba(29, 78, 216, 0.06)" }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0].payload as (typeof data)[number];
                  return (
                    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs shadow">
                      <p className="mb-1 font-semibold text-gray-800">{row.label}</p>
                      <p className="text-gray-700">
                        {row.plays.toLocaleString("en-IN")} plays
                      </p>
                      <p className="text-gray-700">{formatAirtime(row.airtime)}</p>
                      <p className="text-gray-500">
                        {row.screens} screen{row.screens === 1 ? "" : "s"}
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" fill={BAR_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
